import { useInView, Variants } from 'framer-motion';
import { useRef } from 'react';

interface UseScrollRevealOptions {
  /** Trigger animation earlier on mobile (default: true) */
  mobileOptimized?: boolean;
  /** Only animate once (default: true) */
  once?: boolean;
}

/**
 * Custom hook for scroll-triggered reveal animations.
 * Uses a small margin to trigger animations BEFORE elements fully enter viewport,
 * especially important on mobile where users shouldn't see empty sections.
 */
export function useScrollReveal(options: UseScrollRevealOptions = {}) {
  const { mobileOptimized = true, once = true } = options;
  const ref = useRef<HTMLDivElement>(null);
  
  // Use negative margin to trigger animation earlier (before element enters viewport)
  // This prevents the "empty section" problem on mobile
  const inView = useInView(ref, {
    once,
    // Trigger when element is about to enter viewport (100px before on mobile)
    margin: mobileOptimized ? '100px 0px -50px 0px' : '0px',
    // Very low threshold - trigger as soon as any part is visible
    amount: 0.05,
  });

  return { ref, isInView: inView };
}

// Simpler animation variants for better performance
export const fadeInUp: Variants = {
  hidden: { 
    opacity: 0, 
    y: 30,
  },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    }
  },
};

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    }
  },
};

export const staggerContainer: Variants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

export const staggerItem: Variants = {
  hidden: { 
    opacity: 0, 
    y: 20,
  },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut',
    }
  },
};
